import { Button, Grid } from '@mui/material';
import React, { useState } from 'react';
import { Item, Form, DialogSize } from '../utils/sharedTypes';
import { convertItemToPlural } from '../utils/stringManipulation';
import AddItemDialog from './AddItemDialog';
import DeleteItemDialog from './DeleteItemDialog';
import EditItemDialog from './EditItemDialog';
import LinkItemsDialog from './LinkItemsDialog';

interface Props {
  selectedItems: Item[];
  setSelectedItems: React.Dispatch<React.SetStateAction<Item[]>>;
  // Function that evaluates one of selectedItems, returning true if the item may be saved/edited,
  //  and returning false otherwise
  itemValidator: (item: any) => boolean;
  // Instructions displayed when itemValidator has returned false
  formInstructions: string;
  // Function that returns the add/edit form elements; each form element should be controlled, with
  //   a name prop containing a string that matches the field on the form object that that element
  //   changes; the value of each form element should be set to formState.fieldName, where fieldName
  //   matches the string of the name prop; handler should be keyed to relevant onChange props; enterHandler
  //   should be keyed to any relevant onKeyDown props
  childrenCreator: (
    formState: any,
    handler: any,
    enterHandler?: any
  ) => JSX.Element;
  // Initial state for a blank add item form
  addFormInitialState: object;
  itemCreator: Function;
  itemGetter: Function;
  itemUpdater: Function;
  itemDeleter: Function;
  // Function called within the form change handlers; the resulting object will be combined with
  //  the name / value pair from the change event, with the results of the handlerMiddleware function
  //  overriding the name / value pair, if applicable
  handlerMiddleware: (name: string, value: string, state: Form) => object;
  // Optional replacement of the standard Add dialog; ItemOptionsBar will call the function with a boolean flag for the modal status
  //   ('true' means dialog is open), a state setter for the boolean flag, and the childrenCreator function mentioned above; the prop
  //   should return the JSX replacement of the standard Add dialog
  addDialogCreator?: (
    addModalOpen: boolean,
    setAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    childrenCreator: (
      formState: any,
      handler: any,
      enterHandler?: any
    ) => JSX.Element
  ) => JSX.Element;
  // Function to create the table used to link items to the selected order
  tableCreator: (
    setSelectedItems: React.Dispatch<React.SetStateAction<Item[]>>
  ) => JSX.Element;
  // Optional item name that replaces "Item" in dialog headers
  itemName?: string;
  // Item to link the selected items to
  itemToLink: Item;
  // Name of item to link to per API response
  itemToLinkName: string;
  // Name of item ID field
  itemToLinkIdName: string;
  // Name of item to link to for display in dialog
  itemToLinkTitle: string;
  // Optional notes displayed in the Delete dialog only
  deleteNotes?: string;
  // Optional parameter to specify dialog size
  dialogSize?: DialogSize;
}

const ItemOptionsBarWithLinking = ({
  selectedItems,
  setSelectedItems,
  itemValidator,
  formInstructions,
  childrenCreator,
  addFormInitialState,
  itemCreator,
  itemGetter,
  itemUpdater,
  itemDeleter,
  handlerMiddleware,
  addDialogCreator,
  itemToLink,
  itemToLinkName,
  itemToLinkIdName,
  itemToLinkTitle,
  tableCreator,
  itemName,
  deleteNotes,
  dialogSize,
}: Props) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const handleAddModalOpen = () => {
    setAddModalOpen(true);
  };
  const handleEditModalOpen = () => {
    setEditModalOpen(true);
  };
  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
  };
  const handleLinkModalOpen = () => {
    setLinkModalOpen(true);
  };
  const handleUnlink = async () => {
    for (const item of selectedItems) {
      await itemUpdater(
        {
          ...item,
          [itemToLinkName]: null,
          [itemToLinkIdName]: null,
        },
        item.id
      );
    }
  };
  return (
    <Grid container direction='column' spacing={1}>
      <Grid
        container
        justifyContent='flex-start'
        alignItems='center'
        spacing={1}
        style={{ marginBottom: '.5rem' }}
      >
        <Grid item>
          <Button variant='contained' onClick={handleAddModalOpen}>
            Create New {itemName ? itemName : 'Item'}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={handleLinkModalOpen}
            variant='contained'
            color='primary'
          >
            Link {itemToLinkTitle} to Existing{' '}
            {itemName ? convertItemToPlural(itemName) : 'Items'}
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent='flex-start'
        alignItems='center'
        spacing={1}
        style={{ marginBottom: '.5rem' }}
      >
        {selectedItems.length === 1 && (
          <Grid item>
            <Button onClick={() => handleEditModalOpen()} variant='contained'>
              Edit {itemName ? itemName : 'Item'}
            </Button>
          </Grid>
        )}
        {selectedItems.length > 0 && (
          <>
            <Grid item>
              <Button
                onClick={() => handleDeleteModalOpen()}
                variant='contained'
              >
                Delete {itemName ? convertItemToPlural(itemName) : ''}
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={() => handleUnlink()}
                variant='outlined'
                color='secondary'
              >
                Unlink {itemToLinkTitle} from Existing{' '}
                {itemName ? convertItemToPlural(itemName) : 'Items'}
              </Button>
            </Grid>
          </>
        )}
        {!addDialogCreator ? (
          <AddItemDialog
            addModalOpen={addModalOpen}
            setAddModalOpen={setAddModalOpen}
            addFormInitialState={addFormInitialState}
            itemCreator={itemCreator}
            itemValidator={itemValidator}
            formInstructions={formInstructions}
            children={childrenCreator}
            handlerMiddleware={handlerMiddleware}
            itemName={itemName}
            dialogSize={dialogSize}
          />
        ) : (
          addDialogCreator(addModalOpen, setAddModalOpen, childrenCreator)
        )}
        {selectedItems.length === 1 && (
          <EditItemDialog
            editModalOpen={editModalOpen}
            setEditModalOpen={setEditModalOpen}
            itemGetter={itemGetter}
            itemId={selectedItems[0].id}
            itemUpdater={itemUpdater}
            children={childrenCreator}
            handlerMiddleware={handlerMiddleware}
            itemValidator={itemValidator}
            formInstructions={formInstructions}
            itemName={itemName}
            dialogSize={dialogSize}
          />
        )}
        <DeleteItemDialog
          deleteModalOpen={deleteModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          itemDeleter={itemDeleter}
          setSelectedItems={setSelectedItems}
          itemsToDelete={selectedItems}
          itemName={itemName}
          deleteNotes={deleteNotes}
        />
        <LinkItemsDialog
          itemToLink={itemToLink}
          itemToLinkName={itemToLinkName}
          itemToLinkTitle={itemToLinkTitle}
          itemToLinkIdName={itemToLinkIdName}
          itemUpdater={itemUpdater}
          dialogOpen={linkModalOpen}
          setDialogOpen={setLinkModalOpen}
          tableCreator={tableCreator}
        />
      </Grid>
    </Grid>
  );
};

export default ItemOptionsBarWithLinking;
